import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ZONES, zoneLabel } from "@/lib/zones";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Trash2, ArrowRight, HeartPulse } from "lucide-react";
import { PainAlerts } from "@/components/PainAlerts";

const L = {
  en: { title: "Daily Log", subtitle: "How was the session? Track body, mind and pain — every day.",
    session: "Session type", training: "Training", match: "Match", rest: "Rest", other: "Other",
    mood: "Mood", energy: "Energy", soreness: "Soreness", stress: "Stress", focus: "Focus", motivation: "Motivation",
    sleep: "Sleep hours", pain: "Pain zones", notes: "Notes", save: "Save entry",
    trend: "7-day trend", history: "History",
    linkTitle: "Pain flagged", linkBody: "Log it in the Medical map to get an AI protocol for this zone.", openMedical: "Open in Medical", dismiss: "Later" },
  fr: { title: "Suivi Quotidien", subtitle: "Comment s'est passée la séance ? Suis le corps, l'esprit et la douleur — chaque jour.",
    session: "Type de séance", training: "Entraînement", match: "Match", rest: "Repos", other: "Autre",
    mood: "Humeur", energy: "Énergie", soreness: "Courbatures", stress: "Stress", focus: "Concentration", motivation: "Motivation",
    sleep: "Heures de sommeil", pain: "Zones douloureuses", notes: "Notes", save: "Enregistrer",
    trend: "Tendance 7 jours", history: "Historique",
    linkTitle: "Douleur signalée", linkBody: "Enregistre-la dans la Carte Médicale pour obtenir un protocole IA pour cette zone.", openMedical: "Ouvrir dans Médical", dismiss: "Plus tard" },
};

const RANGES = [
  { key: "mood", color: "#00F076" }, { key: "energy", color: "#FFB800" }, { key: "soreness", color: "#FF2E54" },
  { key: "stress", color: "#a78bfa" }, { key: "focus", color: "#60a5fa" }, { key: "motivation", color: "#f472b6" },
];

const INT_KEYS = ["mood", "energy", "soreness", "stress", "focus_level", "motivation"];

export default function Daily() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const empty = { session_type: "training", mood: 7, energy: 7, soreness: 3, stress: 3, focus_level: 7, motivation: 7, sleep_hours: 8, pain_zones: [], notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [linkZones, setLinkZones] = useState([]);
  const [alertKey, setAlertKey] = useState(0);

  const load = () => api.get("/daily").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggleZone = (id) => setForm((f) => ({ ...f, pain_zones: f.pain_zones.includes(id) ? f.pain_zones.filter((z) => z !== id) : [...f.pain_zones, id] }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, sleep_hours: form.sleep_hours ? parseFloat(form.sleep_hours) : null };
      INT_KEYS.forEach((k) => { if (typeof payload[k] === "string") payload[k] = parseInt(payload[k]); });
      await api.post("/daily", payload);
      toast.success("Saved");
      if (form.pain_zones.length > 0) setLinkZones(form.pain_zones);
      setForm(empty);
      await load();
      setAlertKey((k) => k + 1);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/daily/${id}`); load(); };

  const chartData = [...items].slice(0, 7).reverse().map((r) => ({
    day: new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    mood: r.mood, energy: r.energy, soreness: r.soreness, stress: r.stress, focus: r.focus_level, motivation: r.motivation,
  }));

  const Slider = ({ k, label }) => (
    <div>
      <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 flex justify-between">
        <span>{label}</span><span className="text-[#00F076]">{form[k]}/10</span>
      </Label>
      <input type="range" min="1" max="10" value={form[k] ?? 5} onChange={(e) => setForm({ ...form, [k]: parseInt(e.target.value) })} data-testid={`daily-${k}-slider`} className="w-full mt-1 accent-[#00F076]" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="daily-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">MIND · BODY · PAIN</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="daily-title">{t.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>

      <div className="mt-6"><PainAlerts key={alertKey} /></div>

      {linkZones.length > 0 && (
        <div className="mt-6 rounded-xl border border-[#FF2E54]/50 bg-[#FF2E54]/10 p-4 flex flex-wrap items-center gap-3" data-testid="pain-link-banner">
          <HeartPulse className="w-5 h-5 text-[#FF2E54] shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold uppercase text-sm">{t.linkTitle} · <span className="text-[#FF2E54]">{linkZones.map((z) => zoneLabel(z, lang)).join(", ")}</span></div>
            <div className="text-xs text-slate-300 mt-0.5">{t.linkBody}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {linkZones.map((z) => (
              <Button key={z} size="sm" onClick={() => nav(`/medical?zone=${z}`)} data-testid={`pain-link-open-${z}`} className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold text-xs uppercase">
                {t.openMedical}: {zoneLabel(z, lang)} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setLinkZones([])} data-testid="pain-link-dismiss" className="text-slate-400 text-xs">{t.dismiss}</Button>
          </div>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-4">
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.session}</Label>
            <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
              <SelectTrigger data-testid="daily-session-select" className="mt-1 bg-[#090C10] border-[#1E2638] text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121721] border-[#1E2638] text-white">
                <SelectItem value="training">{t.training}</SelectItem>
                <SelectItem value="match">{t.match}</SelectItem>
                <SelectItem value="rest">{t.rest}</SelectItem>
                <SelectItem value="other">{t.other}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Slider k="mood" label={t.mood} />
          <Slider k="energy" label={t.energy} />
          <Slider k="soreness" label={t.soreness} />
          <Slider k="stress" label={t.stress} />
          <Slider k="focus_level" label={t.focus} />
          <Slider k="motivation" label={t.motivation} />
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.sleep}</Label>
            <Input type="number" step="0.5" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })} data-testid="daily-sleep-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.pain} {form.pain_zones.length > 0 && <span className="text-[#FF2E54]">· {form.pain_zones.length}</span>}</Label>
            <div className="mt-1 flex flex-wrap gap-1" data-testid="daily-pain-zones">
              {ZONES.map((z) => {
                const on = form.pain_zones.includes(z.id);
                return (
                  <button key={z.id} type="button" onClick={() => toggleZone(z.id)} data-testid={`daily-pain-${z.id}`}
                    className={`px-2 py-1 rounded text-[10px] font-mono-tech uppercase border transition-colors ${on ? "bg-[#FF2E54] border-[#FF2E54] text-white" : "bg-[#090C10] border-[#1E2638] text-slate-400 hover:text-white"}`}>
                    {z[lang] || z.en}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.notes}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="daily-notes-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="daily-save-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            {saving ? "…" : t.save}
          </Button>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">{t.trend}</div>
            {chartData.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-slate-500 text-sm">Log an entry →</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#1E2638" strokeDasharray="3 3" />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} domain={[0, 10]} />
                  <Tooltip contentStyle={{ background: "#090C10", border: "1px solid #1E2638", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                  {RANGES.map((r) => <Line key={r.key} type="monotone" dataKey={r.key} stroke={r.color} strokeWidth={2} dot={{ r: 3 }} />)}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-lg mb-3">{t.history}</div>
            <div className="space-y-2 max-h-96 overflow-auto">
              {items.length === 0 && <div className="text-slate-500 text-sm">—</div>}
              {items.map((r) => (
                <div key={r.id} className="rounded-lg border border-[#1E2638] bg-[#090C10] p-3" data-testid={`daily-row-${r.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="text-[10px] font-mono-tech tracking-wider uppercase text-slate-400">{new Date(r.created_at).toLocaleString()} · {r.session_type}</div>
                    <Button variant="ghost" size="sm" onClick={() => del(r.id)} className="text-slate-400 hover:text-red-400 h-6 px-2" data-testid={`daily-delete-${r.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs font-mono-tech">
                    {r.mood != null && <span>Mood <b className="text-[#00F076]">{r.mood}</b></span>}
                    {r.energy != null && <span>Eng <b className="text-amber-400">{r.energy}</b></span>}
                    {r.soreness != null && <span>Sor <b className="text-[#FF2E54]">{r.soreness}</b></span>}
                    {r.stress != null && <span>Str <b className="text-purple-400">{r.stress}</b></span>}
                    {r.focus_level != null && <span>Foc <b className="text-blue-400">{r.focus_level}</b></span>}
                    {r.motivation != null && <span>Mot <b className="text-pink-400">{r.motivation}</b></span>}
                    {r.sleep_hours != null && <span>Sleep <b>{r.sleep_hours}h</b></span>}
                  </div>
                  {r.pain_zones?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                      <span className="text-[10px] font-mono-tech uppercase text-red-300">Pain</span>
                      {r.pain_zones.map((z) => (
                        <button key={z} onClick={() => nav(`/medical?zone=${z}`)} data-testid={`daily-row-pain-${r.id}-${z}`}
                          className="text-[10px] font-mono-tech px-1.5 py-0.5 rounded bg-[#FF2E54]/15 border border-[#FF2E54]/40 text-red-200 hover:bg-[#FF2E54]/30">
                          {zoneLabel(z, lang)} →
                        </button>
                      ))}
                    </div>
                  )}
                  {r.notes && <div className="mt-1 text-xs text-slate-400 italic">"{r.notes}"</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
